// Parser for Mindee raw response following the strict field map in the spec.
// Path: receipt.mindee_raw_response.inference.result.fields

const CURRENCY_SYMBOL_MAP = {
  GBP: "£",
  USD: "$",
  EUR: "€",
  INR: "₹",
};

export const mapCurrencySymbol = (code) => {
  if (!code) return "";
  const up = String(code).toUpperCase();
  return CURRENCY_SYMBOL_MAP[up] || up;
};

// Format a YYYY-MM-DD date as "19 Apr 2026"
export const formatLongDate = (isoDate) => {
  if (!isoDate) return null;
  const d = new Date(isoDate);
  if (isNaN(d.getTime())) return null;
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
};

// Keep the time value as-is but strip seconds (e.g. "10:17:42" -> "10:17")
export const formatTime = (timeValue) => {
  if (!timeValue) return null;
  const str = String(timeValue);
  const parts = str.split(":");
  if (parts.length >= 2) return `${parts[0]}:${parts[1]}`;
  return str;
};

export const capitaliseFirst = (s) => {
  if (!s) return s;
  const str = String(s);
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

// Format money using symbol and 2 decimals. Negative preserves sign.
export const formatMoney = (amount, currencyCode) => {
  if (amount === null || amount === undefined || isNaN(Number(amount))) return null;
  const symbol = mapCurrencySymbol(currencyCode);
  const abs = Math.abs(Number(amount)).toFixed(2);
  const sign = Number(amount) < 0 ? "-" : "";
  return `${sign}${symbol}${abs}`;
};

// Merchant avatar colour keyed by first letter of merchant name
export const getMerchantAvatar = (name) => {
  const n = (name || "").trim();
  const initials = n.slice(0, 2).toUpperCase() || "?";
  const firstChar = n.charAt(0).toUpperCase();
  let bg = "#2D5BFF";
  if (/[A-E]/.test(firstChar)) bg = "#0D9488";
  else if (/[F-J]/.test(firstChar)) bg = "#7C3AED";
  else if (/[K-P]/.test(firstChar)) bg = "#16A34A";
  else if (/[Q-Z]/.test(firstChar)) bg = "#D97706";
  return { initials, bg, text: "#FFFFFF" };
};

// Extract all relevant fields from receipt.mindee_raw_response
export const parseReceipt = (receipt) => {
  const fields = receipt?.mindee_raw_response?.inference?.result?.fields || {};

  const supplierName = fields.supplier_name?.value ?? null;
  const supplierAddress = fields.supplier_address?.value ?? null;
  const supplierPhone = fields.supplier_phone_number?.value ?? null;
  const dateRaw = fields.date?.value ?? null;
  const timeRaw = fields.time?.value ?? null;
  const totalAmount = fields.total_amount?.value ?? null;
  const totalNet = fields.total_net?.value ?? null;
  const currencyCode = fields.locale?.fields?.currency?.value ?? null;
  const receiptNumber = fields.receipt_number?.value ?? null;
  const vatItems = fields.supplier_company_registration?.items || [];
  const vatNumber = vatItems.length > 0 ? vatItems[0]?.fields?.number?.value ?? null : null;
  const categoryRaw = fields.purchase_subcategory?.value ?? null;

  const allLineItems = fields.line_items?.items || [];

  const items = [];
  const savings = [];
  allLineItems.forEach((raw, idx) => {
    const description = raw.fields?.description?.value ?? null;
    const quantity = raw.fields?.quantity?.value ?? null;
    const price = raw.fields?.total_price?.value ?? null;
    const priceNum = price === null || price === undefined ? 0 : Number(price);
    const entry = {
      id: idx,
      name: description || "Item",
      quantity: quantity && Number(quantity) !== 0 ? quantity : null,
      price: price,
      priceNum,
    };
    if (priceNum >= 0) items.push(entry);
    else savings.push(entry);
  });

  const totalSaved = savings.reduce((sum, s) => sum + Math.abs(s.priceNum || 0), 0);

  return {
    // merchant/top-level
    merchantName: supplierName || receipt?.merchant || null,
    supplierAddress,
    supplierPhone,
    // dates
    dateRaw,
    dateFormatted: formatLongDate(dateRaw),
    time: formatTime(timeRaw),
    // amounts
    totalAmount,
    totalNet,
    // currency
    currencyCode: currencyCode || receipt?.currency || null,
    currencySymbol: mapCurrencySymbol(currencyCode || receipt?.currency),
    // identifiers
    receiptNumber,
    vatNumber,
    // category
    category: capitaliseFirst(categoryRaw),
    // line items
    items,
    savings,
    totalSaved,
    // fallback image
    imageUrl: receipt?.receipt_image_url || null,
  };
};
